<template>
  <div
    class="inline-flex whitespace-pre-line items-stretch relative text-17px"
    :class="{
      'button-content': !disabled && !loading,
      'ld-button-sm': props.size === 'small',
    }"
    @click="onDisabledClick"
  >
    <Button
      class="flex-1"
      :class="{
        'p-button-option': option,
        'p-button-used': used,
        'p-button-primary': computedSeverity === 'primary',
        'p-button-basic': !outlined,
        'p-button-custom': customColor,
        'p-button-vip': vip,
        'p-button-passionate': props.scene === 'passionate',
        'g-premium-bg': vip,
        'p-button-two-lines': props.twoLines,
      }"
      :label="props.label"
      :loading="props.loading"
      :severity="computedSeverity"
      :outlined="outlined"
      :size="props.size === 'normal' ? undefined : props.size"
      :disabled="props.disabled"
      :icon="props.icon"
      :style="{
        '--custom-color': customColor,
      }"
      @click="onClick"
      @touchstart="onTouchstart"
    >
      <slot></slot>
    </Button>
    <template v-if="vip">
      <LottieBox
        name="shine"
        class="absolute left-0 aspect-square h-full pointer-events-none"
        autoplay
        loop
      />
      <LottieBox
        name="shine"
        class="absolute right-0 aspect-square h-full pointer-events-none"
        autoplay
        loop
      />
    </template>
  </div>
</template>
<script lang="ts">
const BUTTON_SCENES = [
  'primary',
  'secondary',
  'correct',
  'wrong',
  'danger',
  'choice',
  'choiceSelected',
  'choiceCorrect',
  'choiceWrong',
  'choiceUsed',
  'wechat',
  'invite',
  'vip',
  'passionate',
] as const
export type ButtonScene = (typeof BUTTON_SCENES)[number]
export const allButtonScenes: ButtonScene[] = [...BUTTON_SCENES]

const BUTTON_SIZES = ['small', 'normal']
export type ButtonSize = (typeof BUTTON_SIZES)[number]
export const allButtonSizes: ButtonSize[] = [...BUTTON_SIZES]
</script>
<script setup lang="ts">
import Button from 'primevue/button'
import { computed } from 'vue'
import LottieBox from './LottieBox.vue'
import { vibrate } from '@/utils/misc'

const props = withDefaults(
  defineProps<{
    /** 按钮文本 <br> slot 优先，slot 没有的情况下使用 label */
    label?: string

    /** 按钮场景，决定样式和交互行为 */
    scene?: ButtonScene

    /** 是否在加载状态 */
    loading?: boolean

    /** 是否禁用 */
    disabled?: boolean

    /** 按钮 disabled 时仍然可以点击，toast 提示如下消息 */
    disabledMsg?: string

    /** 按钮大小 */
    size?: 'small' | 'normal'

    /**  primevue 图标名，详见 https://primevue.org/icon */
    icon?: string

    /** 两行文本，如果是按钮有两行文本，样式上会有一些差异 */
    twoLines?: boolean
  }>(),
  {
    label: '',
    loading: false,
    disabled: false,
    scene: 'primary',
    size: 'normal',
    icon: '',
    disabledMsg: '',
    twoLines: false,
  }
)

const computedButtonStyle = computed(() => {
  switch (props.scene) {
    case 'primary':
    case 'correct':
    case 'wrong':
    case 'invite':
    case 'wechat':
    case 'vip':
    case 'passionate':
      return 'basic'
    case 'secondary':
    case 'danger':
      return 'outlined'
    case 'choice':
    case 'choiceSelected':
    case 'choiceCorrect':
    case 'choiceWrong':
    case 'choiceUsed':
      return 'option'
    default:
      return 'basic'
  }
})
const computedSeverity = computed(() => {
  switch (props.scene) {
    case 'primary':
      return 'primary'
    case 'correct':
      return 'success'
    case 'wrong':
      return 'danger'
    case 'secondary':
      return 'secondary'
    case 'danger':
      return 'danger'
    case 'choice':
      return 'primary'
    case 'choiceSelected':
      return 'secondary'
    case 'choiceCorrect':
      return 'success'
    case 'choiceWrong':
      return 'danger'
    case 'choiceUsed':
      return 'primary'
    case 'invite':
      return 'primary'
    case 'wechat':
      return 'primary'
    case 'vip':
      return 'primary'
    case 'passionate':
      return 'primary'
    default:
      return 'primary'
  }
})
const customColor = computed(() => {
  if (props.scene === 'invite') {
    return 'var(--cyan-600)'
  }
  if (props.scene === 'wechat') {
    return 'var(--ld-wechat)'
  }
  return ''
})

const outlined = computed(() => computedButtonStyle.value === 'outlined')
const option = computed(() => computedButtonStyle.value === 'option')
const used = computed(() => props.scene === 'choiceUsed')
const vip = computed(() => props.scene === 'vip')

const emit = defineEmits<{
  click: [any]
}>()

function onTouchstart() {
  vibrate()
}

function onClick(evt: MouseEvent) {
  emit('click', evt)
}

function onDisabledClick() {
  if (props.disabled && props.disabledMsg) {
    _message.info(props.disabledMsg)
  }
}
</script>

<style scoped>
.p-button.p-component {
  line-height: 28px;
  --border-bottom-width: 4px;
  font-size: 19px;
  /* 原始 padding 为 上下 10px，左右 16px  */
  /* 这里的 padding 根据 border 和行高来定制的，让文字看起来更加居中 */
  padding: 10px 15px 9px;
}

/* 两行文本 */
.p-button.p-component.p-button-two-lines {
  font-size: 17px;
  line-height: 24px;
  padding: 5px 15px 4px;
}

.p-button.p-button-sm.p-component {
  --border-bottom-width: 2px;
  font-size: 13px;
  line-height: 19px;
  /* 原始 padding 为 上下 5px，左右 8px  */
  /* 这里的 padding 根据 border 和行高来定制的，让文字看起来更加居中 */
  padding: 5px 7px 4px;
}

/* 默认 */
.p-button.p-button-basic,
.p-button.p-button-outlined,
.p-button.p-button-option {
  border-radius: 8px;
}

.p-button.p-button-basic,
.p-button.p-button-basic:hover {
  background-color: var(--ld-brand-500);
  border: 1px solid var(--ld-brand-500);
  border-bottom: var(--border-bottom-width) solid var(--ld-button-border);
}

/* 点击时减小底部 border，看起来有点击的效果，这里加的样式是为了提高优先级来覆盖 primevue 的样式 */
.p-button.p-component.p-button-basic:active {
  border-bottom: 1px solid var(--ld-button-border);
}
.p-button.p-component.p-button.p-button-outlined:active {
  border-bottom: 1px solid var(--ld-button-border);
}

/* 这个 padding 是为了中和底部 border 变小时带来的高度抖动 */
/* 小号按钮由于底部 border 更小，所以补充的 padding 也要小一点 */
.button-content:active {
  padding-top: 3px;
}
.button-content.ld-button-sm:active {
  padding-top: 1px;
}

.p-button.p-component.p-button-outlined,
.p-button.p-component.p-button-outlined:hover {
  color: var(--ld-brand-500);
  background-color: white;
  border: 1px solid var(--ld-neutral-200);
  border-bottom: var(--border-bottom-width) solid var(--ld-neutral-200);
}
.p-button.p-component.p-button-outlined:active {
  border-bottom: 1px solid var(--ld-neutral-200);
}

.p-button.p-button-option,
.p-button.p-button-option:hover {
  color: var(--ld-neutral-600);
  background-color: white;
  border: 1px solid var(--ld-neutral-200);
  border-bottom: var(--border-bottom-width) solid var(--ld-neutral-200);
}
.p-button.p-button-option:active {
  border-bottom: 1px solid var(--ld-neutral-200);
}

/* secondary */
.p-button.p-button-secondary.p-button-basic {
  background-color: white;
  border-top-color: var(--ld-neutral-200);
  border-left-color: var(--ld-neutral-200);
  border-right-color: var(--ld-neutral-200);
}

.p-button.p-button-secondary:not(.p-button-link),
.p-button.p-button-secondary:not(.p-button-link):hover {
  color: var(--text-color-secondary);
}

.p-button.p-button-secondary.p-button-option,
.p-button.p-button-secondary.p-button-option:hover {
  background-color: var(--ld-brand-50);
  color: var(--ld-neutral-600);
  border: 1px solid var(--ld-brand-400);
  border-bottom: var(--border-bottom-width) solid var(--ld-brand-400);
}
.p-button.p-button-secondary.p-button-option:active {
  border-bottom: 1px solid var(--ld-brand-400);
}

/* warning */
.p-button.p-button-warning.p-button-basic {
  background-color: var(--yellow-500);
  border-top-color: var(--yellow-500);
  border-left-color: var(--yellow-500);
  border-right-color: var(--yellow-500);
}

.p-button.p-button-warning.p-button-outlined {
  color: var(--yellow-400);
}

.p-button.p-button-warning.p-button-option,
.p-button.p-button-warning.p-button-option:hover {
  background-color: var(--yellow-50);
  border: 1px solid var(--yellow-400);
  border-bottom: var(--border-bottom-width) solid var(--yellow-400);
}
.p-button.p-button-warning.p-button-option:active {
  border-bottom: 1px solid var(--yellow-400);
}
/* success */
.p-button.p-button-success.p-button-basic {
  background-color: var(--green-500);
  border-top-color: var(--green-500);
  border-left-color: var(--green-500);
  border-right-color: var(--green-500);
}

.p-button.p-button-success.p-button-outlined {
  color: var(--green-400);
}

.p-button.p-button-success.p-button-option,
.p-button.p-button-success.p-button-option:hover {
  color: var(--ld-neutral-600);
  background-color: var(--green-50);
  border: 1px solid var(--green-400);
  border-bottom: var(--border-bottom-width) solid var(--green-400);
}
.p-button.p-button-success.p-button-option:active {
  border-bottom: 1px solid var(--green-400);
}
/* danger */
.p-button.p-button-danger.p-button-basic {
  background-color: var(--red-500);
  border-top-color: var(--red-500);
  border-left-color: var(--red-500);
  border-right-color: var(--red-500);
}

.p-button.p-button-danger.p-button-outlined,
.p-button.p-button-danger.p-button-outlined:hover {
  color: var(--red-400);
}

.p-button.p-button-danger.p-button-outlined:hover {
  border: 1px solid var(--ld-neutral-200);
  border-bottom: var(--border-bottom-width) solid var(--ld-neutral-200);
}

.p-button.p-button-danger.p-button-option,
.p-button.p-button-danger.p-button-option:hover {
  color: var(--ld-neutral-600);
  background-color: var(--red-50);
  border: 1px solid var(--red-400);
  border-bottom: var(--border-bottom-width) solid var(--red-400);
}
.p-button.p-button-danger.p-button-option:active {
  border-bottom: 1px solid var(--red-400);
}

/* used */
.p-button.p-button-used.p-button-option {
  color: transparent;
  background-color: var(--ld-neutral-200);
  border: 1px solid var(--ld-neutral-200);
  border-bottom: var(--border-bottom-width) solid var(--ld-neutral-200);
}
.p-button.p-button-used.p-button-option:active {
  border-bottom: 1px solid var(--ld-neutral-200);
}
/* custom */
.p-button.p-button-custom.p-button-basic {
  background-color: var(--custom-color);
  border-top-color: var(--custom-color);
  border-left-color: var(--custom-color);
  border-right-color: var(--custom-color);
}
.p-button.p-button-custom.p-button-outlined {
  color: var(--custom-color);
}

/* vip */
.p-button.p-button-vip.p-button-basic {
  color: var(--ld-premium-800);
  font-size: 17px;
  font-weight: 600;
  background-repeat: no-repeat;
  border-top-color: transparent;
  border-left-color: transparent;
  border-right-color: transparent;
  border: 1px solid var(--ld-premium-400);
  border-bottom-width: var(--border-bottom-width);
  border-bottom-color: var(--yellow-600);
}

.p-button.p-button-primary.p-button-vip.p-button-basic:hover {
  background: linear-gradient(
    90.73deg,
    var(--ld-premium-300) -0.17%,
    var(--ld-premium-400) 99.85%
  );
  background-color: unset;
}

.p-button.p-button-passionate.p-button-basic {
  background-color: var(--rose-500);
  border-top-color: var(--rose-500);
  border-left-color: var(--rose-500);
  border-right-color: var(--rose-500);
}

/* large */
.p-button.p-button-lg {
  font-size: 21px;
}
</style>
